import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, EmptyState } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { ManagePage } from "@/app/manage/ManagePage";
import { Screen } from "@/shared/ui";
import { SystemMessage } from "@/shared/systemScreens/SystemMessage";
import { landingSurfaceFor } from "@/modules/auth/landing";
import { useSession } from "@/modules/auth/useSession";

/**
 * A URL this app does not have (B32).
 *
 * It replaces a `<Navigate to={`/${DEFAULT_LOCALE}`} />` catch-all that
 * answered every mistyped path by silently rendering the TILL. A manager
 * following a stale bookmark arrived at the floor grid with nothing saying
 * why, and a support person reading "I clicked the report link and got the
 * tables screen" could not tell a routing typo from a permissions problem —
 * the two failures looked identical from the outside.
 *
 * UX-round D61, slice 10.11 — WHAT CHANGED, and what deliberately did not.
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The screen used to do two things at the reader that were written for a
 * support engineer and shown to an owner:
 *
 * 1. It printed the raw path — `/ar/manage/there-is-no-such-page` — at full
 *    width under the message.
 * 2. It spent its second clause explaining the product's own taxonomy: that
 *    this is not a permissions error, and that a permissions error would look
 *    different. *"I do not need the system's taxonomy."*
 *
 * Neither reason was wrong; both were aimed at the wrong person. The path IS
 * what a screenshot has to carry for anyone to fix the bookmark or the e-mail
 * that sent them here — so it is still on the page, one tap away behind a
 * disclosure addressed to whoever is being phoned, rather than shouted at the
 * person who just wanted their report. And the permissions sentence is gone
 * from this screen, where it describes a failure that is not happening: a
 * screen the staff member may not open already says so in its own words, on
 * that screen, checked before it fetches (`apps/restaurant/AGENTS.md`).
 *
 * What replaces the taxonomy is the thing the owner actually asked for —
 * **«ارجع لشاشتك» now says where that is.** `landingSurfaceFor` is the same
 * function `LandingRedirect` uses, so the label names the destination the
 * button really reaches. It falls back to the unnamed label while the session
 * is unread: guessing "the back office" at a cook would be worse than vague.
 *
 * The navigation target is unchanged and stays `/{locale}`, never a hardcoded
 * surface: `LandingRedirect` re-decides by capability at the moment of the tap.
 * The old catch-all's real defect was choosing a surface at all.
 *
 * Read against `docs/contracts/ux-error-localization-contract-v1.md`, which
 * owns this copy: §3 asks a message to state what failed and what to do next
 * (it does: the link is not ours; check it or ask whoever sent it), and §4b
 * keeps one language per screen.
 */
function NotFoundBody() {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale = "ar" } = useParams();
  const { data: session } = useSession();
  const [isDetailOpen, setDetailOpen] = useState(false);

  /*
   * The destination, by name. Only once the session has actually been read —
   * `landingSurfaceFor` answers "manage" for an absent permission list, which
   * is the right place to SEND someone and the wrong thing to promise a cook.
   */
  const surface = session ? landingSurfaceFor(session.seller.permissions) : null;
  const actionLabel =
    surface === "pos"
      ? t((m) => m.notFound.actionPos)
      : surface === "kitchen"
        ? t((m) => m.notFound.actionKitchen)
        : surface === "manage"
          ? t((m) => m.notFound.actionManage)
          : t((m) => m.notFound.action);

  return (
    <div className="flex flex-col gap-3">
      <EmptyState
        title={t((m) => m.notFound.title)}
        description={t((m) => m.notFound.body)}
        actionLabel={actionLabel}
        onAction={() => navigate(`/${locale}`, { replace: true })}
      />
      {/*
       * The path, kept for the support call and addressed to it — closed by
       * default, so the reader meets a sentence rather than a URL.
       *
       * Verbatim and in LTR when opened: a URL is a Latin string and reading
       * one mirrored is how a support call ends with the wrong fix.
       * `break-all` because a long back-office path must wrap rather than push
       * the card wide on a 390px phone.
       */}
      <div className="flex flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={() => setDetailOpen((open) => !open)}
          aria-expanded={isDetailOpen}
          data-testid="not-found-detail-toggle"
          className="min-h-[44px] px-2 text-[11px] font-semibold text-[color:var(--text-muted)] underline"
        >
          {t((m) => m.notFound.detailToggle)}
        </button>
        {isDetailOpen ? (
          <p
            dir="ltr"
            className="break-all text-center text-[11px] font-medium text-[color:var(--text-muted)]"
            data-testid="not-found-path"
          >
            {location.pathname}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Outside the back-office shell — a mistyped `/pos/...`, `/kitchen/...`, or a
 * path with no locale at all. Its own `Screen`, because there is no nav here
 * to be the way back.
 */
export function NotFoundPage() {
  const t = useTranslate();
  return (
    <Screen title={t((m) => m.common.appName)}>
      <NotFoundBody />
    </Screen>
  );
}

/**
 * Inside `ManageShell`, for an unknown `/manage/**` — which is the case B32
 * was actually raised about, and the reason this variant exists: the sidebar
 * and the drawer stay on screen, so the reader can go where they meant to in
 * one tap instead of being thrown out of the back office to find it.
 *
 * Drawn as the design's 404 (`Admin System Screens`, `system-view="404"`): the
 * lilac blob, the "?" cloche, a blue `404` eyebrow, and two ways out — **Go to
 * Today** (`/manage`, where `ManageIndexRedirect` picks the first section the
 * role can see, so the label never promises a screen the role lacks) and
 * **Go back** (only when there is a page behind this one to go back to). The
 * support disclosure with the raw path is kept under them, as before. The
 * sidebar highlights nothing and the top bar reads "Page not found", because
 * `title` is handed up through `ManagePage`'s `hideTitle`.
 */
export function ManageNotFoundPage() {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale = "ar" } = useParams();
  const [isDetailOpen, setDetailOpen] = useState(false);
  // A new route: move focus to its heading so a screen reader hears where it is.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);
  // React Router stamps `idx` on every entry it pushes; 0 (or absent) means
  // this is the first page of the tab and "back" would leave the app.
  const canGoBack = (window.history.state?.idx ?? 0) > 0;

  return (
    <ManagePage title={t((m) => m.systemScreens.notFoundBarTitle)} hideTitle>
      <SystemMessage
        kind="notFound"
        flow="page"
        testId="manage-not-found"
        headingRef={headingRef}
        eyebrow={<span data-ui="system-code">404</span>}
        title={t((m) => m.systemScreens.notFoundTitle)}
        description={t((m) => m.systemScreens.notFoundBody)}
        actions={
          <>
            <Button
              variant="primary"
              onClick={() => navigate(`/${locale}/manage`, { replace: true })}
              data-testid="not-found-today"
            >
              {t((m) => m.systemScreens.notFoundToday)}
            </Button>
            {canGoBack ? (
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                data-testid="not-found-back"
              >
                {t((m) => m.systemScreens.notFoundBack)}
              </Button>
            ) : null}
          </>
        }
        note={
          <>
            <button
              type="button"
              onClick={() => setDetailOpen((open) => !open)}
              aria-expanded={isDetailOpen}
              data-testid="not-found-detail-toggle"
              className="min-h-[44px] px-2 text-[11px] font-semibold text-[color:var(--text-muted)] underline"
            >
              {t((m) => m.notFound.detailToggle)}
            </button>
            {isDetailOpen ? (
              <p
                dir="ltr"
                className="break-all text-center text-[11px] font-medium text-[color:var(--text-muted)]"
                data-testid="not-found-path"
              >
                {location.pathname}
              </p>
            ) : null}
          </>
        }
      />
    </ManagePage>
  );
}
